/**
 * app-hooks.ts — inversion points between the shared layer and features
 * (ADR-002 phase C: "shared публикует хуки, фичи подписываются из своего энтри").
 *
 * shared/ui and shared/system-prompt need a few capabilities that live in
 * feature folders (active session data, generation status, attachment
 * removal, conversation doc-links). Importing them directly would create
 * shared → feature edges, so features register their implementations here
 * on module evaluation instead.
 */

import type { AttachedDoc, ConversationMessage } from './types';

/** The slice of a session runtime the shared layer works with (live object, mutable). */
export interface ActiveConversationSessionView {
  id: string;
  history: ConversationMessage[];
  attachedDocs: AttachedDoc[];
}

let activeSessionProvider: () => ActiveConversationSessionView | null = () => null;

/** Registered by session/session-store.ts. */
export function registerActiveSessionProvider(fn: () => ActiveConversationSessionView | null): void {
  activeSessionProvider = fn;
}

export function getActiveConversationSession(): ActiveConversationSessionView | null {
  return activeSessionProvider();
}

let generatingCheck: () => boolean = () => false;

/** Registered by generation-manager.ts. */
export function registerGeneratingCheck(fn: () => boolean): void {
  generatingCheck = fn;
}

export function isGenerationActive(): boolean {
  return generatingCheck();
}

let attachmentRemover: ((sessionId: string, index: number) => void) | null = null;

/** Registered by session/session-attachments.ts. */
export function registerAttachmentRemover(fn: (sessionId: string, index: number) => void): void {
  attachmentRemover = fn;
}

export function removeSessionAttachmentViaHook(sessionId: string, index: number): boolean {
  if (!attachmentRemover) return false;
  attachmentRemover(sessionId, index);
  return true;
}

let conversationSessionOpener: ((sessionId: string) => void | Promise<void>) | null = null;

/** Registered by session/sessions.ts. Lets a product (avg-dialog: «Сохранить в
 * Разговор») open a chat in the Conversation view without importing the
 * session feature sideways — that edge is a hard gate (no-sideways-features). */
export function registerConversationSessionOpener(
  fn: (sessionId: string) => void | Promise<void>
): void {
  conversationSessionOpener = fn;
}

export async function openConversationSession(sessionId: string): Promise<boolean> {
  if (!conversationSessionOpener) return false;
  await conversationSessionOpener(sessionId);
  return true;
}

type MarkdownEnhancer = (el: HTMLElement) => void;
const markdownEnhancers: MarkdownEnhancer[] = [];

/** Registered by kb/books-nav.ts (conversation doc-links). Runs after each markdown render. */
export function registerMarkdownEnhancer(fn: MarkdownEnhancer): void {
  markdownEnhancers.push(fn);
}

export function applyMarkdownEnhancers(el: HTMLElement): void {
  for (const fn of markdownEnhancers) fn(el);
}
